import { useBundle } from '@amzn/react-arb-tools';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Card } from '@amzn/stencil-react-components/card';
import { Col, Row, Spacer } from '@amzn/stencil-react-components/layout';
import {
    MessageBanner,
    MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import {
    Spinner,
    SpinnerColorScheme,
    SpinnerSize,
} from '@amzn/stencil-react-components/spinner';
import {
    FormWrapper,
    LabelPosition,
} from '@amzn/stencil-react-components/form';
import {
    InputHeader,
    InputWrapper,
} from '@amzn/stencil-react-components/input';
import { Radio } from '@amzn/stencil-react-components/radio';
import { H1, Text, TextVariant } from '@amzn/stencil-react-components/text';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { trackClick } from 'src/analytics/clickTracking';
import { recordRumEvent } from 'src/analytics/rum';
import { UNSUBSCRIBE_MESSAGE_BUNDLE_NAME } from 'src/constants';
import {
    PAGE_LOAD_EVENT,
    UNSUBSCRIBE_PAGE_GROUP,
} from 'src/constants/MonitoringConstants';
import { useUnsubscribe } from 'src/hooks/useUnsubscribe';
import { highlightedText } from 'src/utils/textUtils';
import { t } from '../helpers';

const UNSUBSCRIBE_REASONS = [
    { value: 'RELEVANCY', text: 'unsubscribe_reason_relevancy' },
    { value: 'PROMOTIONAL', text: 'unsubscribe_reason_promotional' },
    { value: 'INTEREST', text: 'unsubscribe_reason_interest' },
    { value: 'TOO_MANY', text: 'unsubscribe_reason_too_many' },
];

const REASON_GROUP_ID = 'unsubscribe-reason-id';

const UnsubscribePage = () => {
    const [bundle] = useBundle(UNSUBSCRIBE_MESSAGE_BUNDLE_NAME);
    const { subscriptionId } = useParams();
    const { mutateAsync: unsubscribeMutation } = useUnsubscribe('unsubscribe_page');

    const [reason, setReason] = useState<string | null>(null);
    const [unsubscribed, setUnsubscribed] = useState<boolean>(false);
    const [unsubscribeFailed, setUnsubscribeFailed] = useState<boolean>(false);
    const [unsubscribeLoading, setUnsubscribeLoading] = useState<boolean>(false);

    useEffect(() => {
        recordRumEvent(PAGE_LOAD_EVENT, { pageGroup: UNSUBSCRIBE_PAGE_GROUP });
    }, []);

    const unsubscribe = async (): Promise<void> => {
        if (unsubscribeLoading) {
            return;
        }
        
        trackClick({
            elementId: 'unsubscribe',
            elementType: 'button',
            pageGroup: UNSUBSCRIBE_PAGE_GROUP,
            metadata: { reason: reason ?? 'UNKNOWN' },
        });

        setUnsubscribeLoading(true);
        setUnsubscribeFailed(false);

        await unsubscribeMutation({ subscriptionId: subscriptionId ?? '', reason })
            .then(() => setUnsubscribed(true))
            .catch(() => setUnsubscribeFailed(true))
            .finally(() => setUnsubscribeLoading(false));
    };

    const header = highlightedText(
        bundle,
        unsubscribed
            ? 'unsubscribe_success_header_prefix'
            : 'unsubscribe_header_prefix',
        'unsubscribe_header_in_this_together',
        'unsubscribe_header_postfix',
    );

    return (
        <Col
            dataTestId="grow-thrive-connect-unsubscribe-test-id"
            backgroundColor="color.surface.dark-bg-static"
            alignItems="center"
            padding="dimensions.spacing.700"
        >
            <Card maxWidth={640} width="100%">
                <Col
                    width="100%"
                    gridGap="dimensions.spacing.300"
                    padding="dimensions.spacing.400"
                >
                    {unsubscribeFailed && (
                        <MessageBanner
                            type={MessageBannerType.Error}
                            aria-live="assertive"
                            isDismissible
                            dataTestId="unsubscribe-error-banner-test-id"
                            onDismissed={() => setUnsubscribeFailed(false)}
                        >
                            <Text>{t(bundle, 'unsubscribe_failure')}</Text>
                        </MessageBanner>
                    )}
                    {!unsubscribed ? (
                        <>
                            <H1 variant={TextVariant.HeadingMD}>{header}</H1>
                            <Text>
                                {t(bundle, 'unsubscribe_description')}
                            </Text>
                            <FormWrapper width="100%">
                                <fieldset>
                                    <legend>
                                        <InputHeader
                                            labelText={
                                                t(bundle, 'unsubscribe_reason_prompt') || 'unsubscribe_reason_prompt'
                                            }
                                            htmlFor={REASON_GROUP_ID}
                                        />
                                    </legend>
                                    <Spacer height="dimensions.spacing.200" />
                                    <FormWrapper
                                        id={REASON_GROUP_ID}
                                        rowGap="8px !important" // form wrapper within a form wrapper
                                    >
                                        {UNSUBSCRIBE_REASONS.map((unsubscribeReason, index) => (
                                            <InputWrapper
                                                id={`${REASON_GROUP_ID}-${index}`}
                                                key={unsubscribeReason.value}
                                                labelText={
                                                    t(bundle, unsubscribeReason.text) ||
                                                    unsubscribeReason.text
                                                }
                                                labelPosition={LabelPosition.Trailing}
                                            >
                                                {({
                                                    'aria-required': ariaRequired,
                                                    'aria-invalid': ariaInvalid,
                                                    ...inputProps
                                                }) => (
                                                    <Radio
                                                        {...inputProps}
                                                        dataTestId={`unsubscribe-reason-${unsubscribeReason.value}-test-id`}
                                                        name="unsubscribeReason"
                                                        value={unsubscribeReason.value}
                                                        checked={reason === unsubscribeReason.value}
                                                        onChange={() => setReason(unsubscribeReason.value)}
                                                    />
                                                )}
                                            </InputWrapper>
                                        ))}
                                    </FormWrapper>
                                </fieldset>
                                <Row>
                                    <Button
                                        variant={ButtonVariant.Primary}
                                        dataTestId="unsubscribe-button-test-id"
                                        onClick={() => unsubscribe()}
                                    >
                                        {unsubscribeLoading && (
                                            <Spinner
                                                size={SpinnerSize.Small}
                                                style={{ marginRight: '8px' }}
                                                colorScheme={SpinnerColorScheme.Inverted}
                                            />
                                        )}
                                        <Text textAlign="center">
                                            {t(bundle, 'unsubscribe_button')}
                                        </Text>
                                    </Button>
                                </Row>
                            </FormWrapper>
                        </>
                    ) : (
                        <Text variant={TextVariant.HeadingMD}>{header}</Text>
                    )}
                </Col>
            </Card>
        </Col>
    );
};

export default UnsubscribePage;
